import { useMemo, useRef, useState } from "react";
import { ColDef, ColumnState, ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import usePDA1081_R01Store from "./PDA1081_R01Store";
import type { ReturnItem } from "./PDA1081_R01Dto";
import {
  aggridHeaderClass,
  pdaCellStyle,
} from "src/components/common/common";
import { usePDA1081_R01Content } from "./PDA1081_R01Content";

type RowType = ReturnItem & { inserted?: boolean };

const PDA1081_R01Table = () => {
  const { returnItems } = usePDA1081_R01Store();
  const { handleRemoveFromGrid } = usePDA1081_R01Content();
  const gridRef = useRef<AgGridReact<RowType>>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [, setHoveredRowIndex] = useState<number | null>(null);
  const [, setColumnState] = useState<ColumnState[] | null>(null);

  const rowData = useMemo(() => [...returnItems] as RowType[], [returnItems]);
  const totalQty = returnItems.reduce((s, i) => s + (i.returnQty || 0), 0);

  const pinnedBottom = useMemo(
    () => [{ itemNm: "총계", returnQty: totalQty } as RowType],
    [totalQty]
  );

  const columnDefs = useMemo<ColDef<RowType>[]>(() => {
    const left = {
      textAlign: "left",
      ...pdaCellStyle,
    } as const;
    const right = {
      textAlign: "right",
      ...pdaCellStyle,
      justifyContent: "flex-end",
    } as const;
    const center = {
      textAlign: "center",
      ...pdaCellStyle,
      justifyContent: "center",
    } as const;
    return [
      {
        headerName: "품명",
        field: "itemNm",
        valueGetter: (p) => (p.node?.rowPinned === "bottom" ? "총계" : p.data?.itemNm ?? "-"),
        cellStyle: left,
        headerClass: aggridHeaderClass,
        flex: 2,
        minWidth: 100,
      },
      {
        headerName: "규격",
        field: "itemSpec",
        valueGetter: (p) => (p.node?.rowPinned === "bottom" ? "" : p.data?.itemSpec ?? "-"),
        cellStyle: left,
        headerClass: aggridHeaderClass,
        flex: 1.5,
        minWidth: 80,
      },
      {
        headerName: "반입량",
        field: "returnQty",
        valueGetter: (p) => p.data?.returnQty ?? 0,
        valueFormatter: (p: ValueFormatterParams) =>
          p.value == null ? "-" : Number(p.value).toLocaleString(),
        cellStyle: { ...right, fontWeight: "bold", color: "#2563eb" },
        headerClass: aggridHeaderClass,
        flex: 1.2,
        minWidth: 80,
      },
      {
        headerName: "삭제",
        field: "itemCd",
        valueGetter: () => "",
        cellRenderer: (p: { node?: { rowPinned?: string }; data?: RowType }) => {
          if (p.node?.rowPinned === "bottom" || !p.data?.itemCd) return "";
          return (
            <button
              type="button"
              onClick={() => handleRemoveFromGrid(p.data!.itemCd)}
              className="text-red-500 font-bold"
              title="삭제"
            >
              ✕
            </button>
          );
        },
        cellStyle: center,
        headerClass: aggridHeaderClass,
        flex: 0.6,
        minWidth: 50,
      },
    ];
  }, [handleRemoveFromGrid]);

  return (
    <div className="flex-1 p-3 pt-2 flex w-full h-full flex-col overflow-hidden">
      <GridContextWrapper
        gridRef={gridRef}
        gridWrapperRef={gridWrapperRef}
        paddedGridData={rowData}
        initSelected={() => {}}
        baseColumnDefs={columnDefs}
        setColumnState={setColumnState}
        localStorageKey="PDA1081_R01_ColumnState"
        enableDataHistory={false}
      >
        <div className="flex-1 w-full h-full min-h-0">
          <Grid_Select<RowType>
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            setHoveredRowIndex={setHoveredRowIndex}
            columnStateKey="PDA1081_R01_ColumnState"
            setColumnState={setColumnState}
            pinnedBottomRowData={pinnedBottom}
          />
        </div>
      </GridContextWrapper>
    </div>
  );
};

export default PDA1081_R01Table;
